import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPhone, FiSettings, FiUsers, FiMessageSquare, FiClock, FiGlobe} = FiIcons;

const Features = () => {
  const features = [
    {
      icon: FiPhone,
      title: 'Advanced PBX & VoIP Integration',
      description: 'Our AI voice receptionist seamlessly integrates with your existing PBX systems, including 3CX, Asterisk, and modern VoIP infrastructure. No disruption to your current setup means faster deployment and immediate productivity gains for your business operations.',
      keywords: 'PBX integration, VoIP compatibility, 3CX systems, Asterisk support'
    },
    {
      icon: FiSettings,
      title: 'Advanced AI Voice Technology',
      description: 'Powered by cutting-edge artificial intelligence, our voice assistant provides natural, human-like conversations with advanced natural language processing. The system learns from each interaction to continuously improve call handling and customer satisfaction.',
      keywords: 'AI voice assistant, automated call handling, natural language processing'
    },
    {
      icon: FiUsers,
      title: 'Intelligent Call Routing & Transfers',
      description: 'Smart call routing ensures every caller reaches the right department or person efficiently. Our AI understands context, urgency, and caller intent to make informed routing decisions, reducing wait times and improving customer experience.',
      keywords: 'call transfers, intelligent routing, call distribution, customer experience'
    },
    {
      icon: FiMessageSquare,
      title: 'Voicemail & CRM Integration',
      description: 'Automated voicemail transcription with instant delivery to your email and seamless CRM integration keeps your customer data organized and accessible. Compatible with Salesforce, HubSpot, and other popular customer relationship management systems.',
      keywords: 'voicemail transcription, CRM integration, Salesforce, HubSpot'
    },
    {
      icon: FiGlobe,
      title: 'Comprehensive Multilingual Support',
      description: 'Serve your diverse NYC and Hudson Valley clientele with support for over 50 languages and dialects. Our AI understands regional accents and cultural nuances, making it perfect for businesses serving multicultural communities across New York.',
      keywords: 'multilingual support, NYC businesses, language support, cultural adaptation'
    },
    {
      icon: FiClock,
      title: '24/7 Professional Business Service',
      description: 'Never miss another important call with round-the-clock professional service. Perfect AI receptionist solution for small businesses, medical practices, law firms, and service providers who need consistent availability without the overhead of hiring additional staff.',
      keywords: 'AI receptionist for small business, 24/7 support, professional service, cost-effective'
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Why Choose Our AI Voice Receptionist Technology?
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.1}}
            className="text-xl text-gray-600 max-w-4xl mx-auto mb-8"
          >
            Advanced artificial intelligence technology designed specifically for NYC and Hudson Valley businesses. From automated call handling to seamless PBX integration, our comprehensive solution transforms how your business communicates with customers.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-lg text-gray-700 max-w-5xl mx-auto leading-relaxed"
          >
            <p className="mb-4">
              Our AI voice receptionist system serves businesses across Manhattan, Brooklyn, Queens, the Bronx, and throughout the Hudson Valley region including Poughkeepsie, Kingston, Newburgh, and surrounding areas. Whether you operate a medical practice, law firm, real estate agency, accounting firm, or any service-based business, our intelligent voice assistant provides professional customer service that enhances your reputation.
            </p>
            <p>
              With sophisticated natural language processing, multilingual capabilities, and intelligent call routing, our AI receptionist integrates seamlessly with your existing telecommunications infrastructure while providing cost-effective scalability for growing businesses throughout New York State.
            </p>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.1}}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {feature.description}
              </p>
              <div className="text-xs text-blue-600 font-medium">
                {feature.keywords}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional SEO Content Section */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.3}}
          className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Complete AI Voice Solution for New York Businesses
          </h3>
          <div className="grid md:grid-cols-2 gap-8 text-gray-700">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Industry-Specific Solutions</h4>
              <p className="mb-4">
                Our AI voice receptionist technology is specifically configured for various industries throughout NYC and the Hudson Valley. Medical practices benefit from HIPAA-compliant call handling and appointment scheduling. Law firms receive sophisticated call screening and client intake management. Real estate agencies get lead capture and property inquiry routing.
              </p>
              <p>
                Small businesses, accounting firms, dental offices, veterinary clinics, and professional service providers all benefit from our customizable AI voice solutions that understand industry-specific terminology and workflows.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Advanced Technology Features</h4>
              <p className="mb-4">
                Our AI voice assistant utilizes machine learning, natural language understanding, and sentiment analysis to provide human-like interactions. The system handles complex conversations, understands context and intent, and can escalate calls when necessary.
              </p>
              <p>
                Integration capabilities include popular CRM systems, calendar applications, and business management tools. Real-time analytics and reporting provide insights into call patterns, customer satisfaction, and business performance metrics.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.4}}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Transform Your Business Communications?
            </h3>
            <p className="text-blue-100 mb-6 max-w-3xl mx-auto">
              Join hundreds of NYC and Hudson Valley businesses using our AI voice assistant to provide exceptional customer service 24/7. Experience the difference that professional, intelligent call handling can make for your business growth and customer satisfaction.
            </p>
            <button
              onClick={() => document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})}
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              View Pricing Plans
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;